/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
"use client";
import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  FileText,
  Eye,
  Send,
  BarChart3,
  Layers,
  Settings2,
  Calculator,
  DollarSign,
  Briefcase,
  Users,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "Proposals & Client Portal",
  title: "Send clear proposals and",
  titleHighlight: "close work faster.",
  description:
    "Build a professional proposal from your estimate, send it quickly, and give clients a simple portal experience to review, respond, and move forward.",

  featureGrid: {
    title: "Everything in the proposal workflow",
    items: [
      {
        icon: FileText,
        title: "Professional Layouts",
        description:
          "Use clean branded formats so your proposal looks credible on every bid.",
      },
      {
        icon: Settings2,
        title: "Inline Editing",
        description:
          "Edit sections directly in the proposal preview with a floating toolbar.",
      },
      {
        icon: Send,
        title: "Email Send",
        description:
          "Send proposals directly from the estimate page. Pre-populated with client contact.",
      },
      {
        icon: Eye,
        title: "Client Portal",
        description:
          "Clients view, approve, or decline — with optional feedback on declined bids.",
      },
      {
        icon: BarChart3,
        title: "Engagement Tracking",
        description:
          "See when clients view and interact with proposals so follow-up timing is stronger.",
      },
      {
        icon: Layers,
        title: "Proposal Updates",
        description:
          "Keep updated proposal versions organized so your team and client stay aligned on current scope and pricing.",
      },
    ],
  },

  workflow: {
    title: "From estimate to signed contract",
    description:
      "A straightforward flow for producing cleaner proposals and getting client decisions faster.",
    steps: [
      {
        label: "Prepare",
        title: "Build a proposal from your existing estimate",
        description:
          "Start from estimate data you already trust so proposal creation is fast and consistent across your team.",
        details: [
          "Estimate-to-proposal continuity",
          "Scope and pricing carryover",
          "Less manual formatting",
          "Faster turnaround to send",
        ],
      },
      {
        label: "Customize",
        title: "Tailor the proposal for this client",
        description:
          "Adjust messaging, detail level, and structure so the proposal fits the client conversation and project type.",
        details: [
          "Client-appropriate pricing detail choices",
          "Section-level content refinement",
          "Consistent branded presentation",
          "Clearer proposal communication",
        ],
      },
      {
        label: "Send",
        title: "Deliver and monitor proposal engagement",
        description:
          "Send proposals with better visibility into engagement so sales follow-up can happen at the right moment.",
        details: [
          "Client-focused send workflow",
          "Send-time version consistency",
          "Engagement signal visibility",
          "Revision-aware resend support",
        ],
      },
      {
        label: "Decide",
        title: "Collect approval or feedback in the portal",
        description:
          "Give clients a simple way to review and respond so approvals are smoother and lost deals provide useful feedback.",
        details: [
          "Portal-based proposal review",
          "Clear approve/decline flow",
          "Feedback capture on declines",
          "Faster response awareness for your team",
        ],
      },
    ],
  },

  capabilities: [
    {
      icon: Eye,
      title: "Client Portal",
      description:
        "Give clients a cleaner experience to review proposals and stay aligned without endless email back-and-forth.",
      bullets: [
        "Central proposal and document visibility",
        "Clear client decision workflow",
        "Reduced communication friction",
        "Better client confidence in your process",
      ],
    },
    {
      icon: BarChart3,
      title: "Know When To Follow Up",
      description:
        "Use proposal and client context to prioritize follow-ups and improve close rates.",
      bullets: [
        "Stronger follow-up timing",
        "Win-rate visibility by lead and proposal stage",
        "Client history-driven outreach",
        "Better sales execution rhythm",
      ],
    },
    {
      icon: Send,
      title: "See When Clients Are Reading",
      description:
        "Know when clients are actively reviewing so your next call lands at the right time.",
      bullets: [
        "Proposal activity visibility",
        "Sent vs viewed response context",
        "Stronger follow-up prioritization",
        "Less guesswork for sales teams",
      ],
    },
    {
      icon: FileText,
      title: "PDF Export",
      description:
        "Export polished branded proposal documents that are client-ready and easy to share.",
      bullets: [
        "Multiple presentation styles",
        "Consistent brand appearance",
        "Scope and pricing clarity",
        "Professional send-ready output",
      ],
    },
  ],

  connections: {
    title: "How proposals connect to the platform",
    items: [
      {
        icon: Calculator,
        flow: "Estimate → Proposal",
        description:
          "Core line items, scope, and pricing carry over from the estimate automatically.",
      },
      {
        icon: Briefcase,
        flow: "Approval → Project",
        description:
          "When the client approves, the estimate moves to won so project creation can start with aligned budget context.",
      },
      {
        icon: Users,
        flow: "Client → Portal",
        description:
          "Clients get a dedicated portal to review proposals, track decisions, and access shared documents.",
      },
      {
        icon: Eye,
        flow: "Proposal Activity → Sales Follow-Up",
        description:
          "Proposal activity signals help sales teams prioritize follow-up timing and focus on active opportunities.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Estimates & Takeoffs",
      href: "/features/estimates",
      icon: Calculator,
    },
    {
      label: "Budgets & Invoices",
      href: "/features/budgets-invoices",
      icon: DollarSign,
    },
    { label: "Client Management", href: "/features/crm", icon: Users },
    {
      label: "Projects & Services",
      href: "/features/project-management",
      icon: Briefcase,
    },
  ],
};

export function ProposalsPortalPageClient() {
  return <FeaturePageTemplate config={config} />;
}
